import { differenceInCalendarDays, format, isToday, isTomorrow } from 'date-fns';
import { isString } from 'lodash';

import {
  type BillingCyclePricing,
  type PlanPricing,
  type Subscription,
  type SubscriptionBillingCycle,
  type SubscriptionPlan,
} from '@air/api/types';
import { BYTES_GIGABYTE, parseAirDateToISO } from '@air/utilities';
import { reportErrorToBugsnag } from '@air/utils-error';

import { PLAN_CYCLE_MONTHS } from '~/constants/planCycle';
import { getSubscriptionCreditPackInfo, type SubscriptionCreditPackInfo } from '~/utils/creditUtils';

export type EnhancedSubscription = {
  /** Formatted display name for the plan (e.g. `"Starter"`). */
  planName: string;
  hasActiveSubscription: boolean;
  isInFreePlan: boolean;
  isOnPlusPlan: boolean;
  isOnCreatorPlan: boolean;
  isOnStarterPlan: boolean;
  isOnProPlan: boolean;
  isOnBusinessPlan: boolean;
  isFixedModel: boolean;
  isPerMemberModel: boolean;
  isCreditModel: boolean;
  isOnEnterprisePlan: boolean;
  isOnPaidPlan: boolean;
} & Subscription;

// `demo` belongs to every tier: demo workspaces exist to demo the product, so they get each tier's
// capabilities regardless of what they'd pay for.
//
// This means demo also takes the Enterprise branch of billing copy — "Contact support" instead of
// "Upgrade plan" — on the surfaces it can still reach (the storage CTA in the workspace header, the
// credit-depletion modal and alerts). That's the better of two imperfect options: `useSubscriptionPlan`
// is disabled for demo because the endpoint throws `PLAN_NOT_FOUND`, so the upgrade path leads to a
// plans modal that can't identify the workspace's current plan.
export const ENTERPRISE_PLANS: Subscription['plan'][] = ['enterprise', 'demo'];
export const BUSINESS_PLANS: Subscription['plan'][] = ['business', 'enterprise', 'demo'];
export const PRO_PLANS: Subscription['plan'][] = ['pro', 'business', 'demo', 'enterprise'];
export const PAID_PLANS: Subscription['plan'][] = [
  'creator',
  'starter',
  'plus',
  'pro',
  'business',
  'demo',
  'enterprise',
];

export const buildEnhancedSubscription = (subscription: Subscription): EnhancedSubscription => ({
  ...subscription,
  planName: subscriptionPlanMap[subscription.plan],
  // The backend is currently sending down a string for `usedStorage` instead of a number sometimes
  // (it has to do with caching).
  usedStorage: isString(subscription.usedStorage) ? parseInt(subscription.usedStorage, 10) : subscription.usedStorage,
  hasActiveSubscription: subscription.status === 'active',
  isFixedModel: subscription.pricingModel === 'fixed',
  isPerMemberModel: subscription.pricingModel === 'perMember',
  isInFreePlan: subscription.plan === 'basic',
  isOnCreatorPlan: subscription.plan === 'creator',
  isOnStarterPlan: subscription.plan === 'starter',
  isOnPlusPlan: subscription.plan === 'plus',
  isOnBusinessPlan: BUSINESS_PLANS.includes(subscription.plan),
  isCreditModel: subscription.pricingModel === 'credit',
  isOnEnterprisePlan: ENTERPRISE_PLANS.includes(subscription.plan),
  isOnProPlan: PRO_PLANS.includes(subscription.plan),
  isOnPaidPlan: PAID_PLANS.includes(subscription.plan),
});

type SubscriptionPlanDisplayName = 'Free' | 'Creator' | 'Starter' | 'Plus' | 'Pro' | 'Business' | 'Enterprise' | 'Demo';

export const DEFAULT_TRIAL_DURATION_DAYS = 7;

export const subscriptionPlanMap: { [key in SubscriptionPlan]: SubscriptionPlanDisplayName } = {
  basic: 'Free',
  creator: 'Creator',
  starter: 'Starter',
  plus: 'Plus',
  pro: 'Pro',
  business: 'Business',
  enterprise: 'Enterprise',
  demo: 'Demo',
};

export const getTrialDurationDays = (purchaseDate: string, trialEndDate: string): number => {
  const start = new Date(parseAirDateToISO(purchaseDate));
  const end = new Date(parseAirDateToISO(trialEndDate));

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return DEFAULT_TRIAL_DURATION_DAYS;
  }

  const days = differenceInCalendarDays(end, start);

  return days > 0 ? days : DEFAULT_TRIAL_DURATION_DAYS;
};

/**
 * Checks if a subscription is currently on a trial.
 * @param subscription - The subscription object to check
 * @returns True if the subscription has a trial end date in the future
 */
export const isSubscriptionOnTrial = (subscription: { trialEndDate?: string | null } | undefined): boolean => {
  if (!subscription?.trialEndDate) {
    return false;
  }

  const trialEnd = new Date(parseAirDateToISO(subscription.trialEndDate));

  if (Number.isNaN(trialEnd.getTime())) {
    reportErrorToBugsnag({
      error: new Error('Invalid trial end date'),
      context: 'isSubscriptionOnTrial',
      metadata: { trialEndDate: subscription.trialEndDate },
    });
    return false;
  }

  return trialEnd > new Date();
};

/**
 * Formats a trial end date from Air's backend format to a human-readable string.
 * Shows "today" or "tomorrow" for dates within the next 2 days, otherwise shows full date.
 * @param trialEndDate - The trial end date string from the backend (Postgres timestamp format)
 * @returns Formatted date string like "today", "tomorrow", "November 20, 2025", or empty string if invalid
 */
export const formatTrialEndDate = (trialEndDate: string): string => {
  const date = new Date(parseAirDateToISO(trialEndDate));

  if (Number.isNaN(date.getTime())) {
    reportErrorToBugsnag({
      error: new Error('Invalid trial end date'),
      context: 'formatTrialEndDate',
      metadata: { trialEndDate },
    });
    return '';
  }

  if (isToday(date)) {
    return 'today';
  }
  if (isTomorrow(date)) {
    return 'tomorrow';
  }

  return format(date, 'MMMM d, yyyy');
};

type StorageUpgradeParams = {
  subscription?: EnhancedSubscription;
  storage?: { isOutOfStorage?: boolean; isAlmostOutOfStorage?: boolean };
};

/**
 * Determines if the storage upgrade CTA should be shown.
 * Shows for free plan users or pro plan users who are out of or almost out of storage.
 * @param subscription - Subscription information
 * @param storage - Storage information
 * @returns True if storage upgrade CTA should be shown
 */
export const shouldShowStorageUpgrade = ({ subscription, storage }: StorageUpgradeParams): boolean => {
  if (subscription?.isCreditModel) {
    return false;
  }

  if (subscription?.isInFreePlan) {
    return true;
  }

  if (subscription?.isOnProPlan) {
    return storage?.isOutOfStorage || storage?.isAlmostOutOfStorage || false;
  }

  return false;
};

export const isBillingCycle = (value: string): value is SubscriptionBillingCycle => {
  const BILLING_CYCLES = ['monthly', 'annual', 'quarter', 'semiAnnual'] satisfies SubscriptionBillingCycle[];

  return BILLING_CYCLES.some((cycle) => cycle === value);
};

export type StorageOverageInfo = {
  /** Number of additional-storage units (typically TB) the workspace is over its limit by.
   *  `0` when within limits. */
  units: number;
  /** Per-unit price for the cycle, in cents. */
  pricePerUnitCents: number;
  /** Aggregate overage cost for the cycle (`units * pricePerUnitCents`), in cents. */
  totalPriceCents: number;
};

const getStorageOverageInfo = ({
  subscription,
  plan,
  billingCyclePricing,
}: {
  subscription: Subscription;
  plan: PlanPricing;
  billingCyclePricing: BillingCyclePricing | null | undefined;
}): StorageOverageInfo | null => {
  if (!subscription.trackAdditionalStorageCharges || !billingCyclePricing) {
    return null;
  }
  if (typeof subscription.usedStorage !== 'number' || typeof subscription.includedGb !== 'number') {
    return null;
  }
  if (!plan.additionalStorage || !billingCyclePricing.additionalStoragePrice) {
    return null;
  }

  const includedBytes = subscription.includedGb * BYTES_GIGABYTE;
  const overBytes = Math.max(subscription.usedStorage - includedBytes, 0);
  const units = Math.ceil(overBytes / plan.additionalStorage);

  return {
    units,
    pricePerUnitCents: billingCyclePricing.additionalStoragePrice,
    totalPriceCents: units * billingCyclePricing.additionalStoragePrice,
  };
};

export type SubscriptionInfo = {
  /** Billing cycle of the subscription, validated against the plan's available cycles. `null`
   *  for free subs (which aren't on a cycle). */
  billingCycle: SubscriptionBillingCycle | null;
  /** Plan price for the locked cycle, in cents. For perMember plans this is the *per-seat*
   *  rate, not the total — see `totalPriceCents` for the aggregate. `0` for free subs. */
  planPriceCents: number;
  /** Monthly credits granted by this subscription's current cycle (excluding packs).
   *  Read from `subscription.creditsPerCycle`, not the plan default, so legacy/custom
   *  accounts whose granted credits diverge from the published tier report correctly. */
  planCredits: number;
  /** Subscription credit pack information. */
  creditPack: SubscriptionCreditPackInfo | null;
  /** Total monthly credits across plan + packs. */
  totalCredits: number;
  /** Total recurring price for the cycle (plan + packs), in cents. For perMember plans this
   *  multiplies the plan price by `totalBillableSeats` (when provided). `0` for free subs. */
  totalPriceCents: number;
  /** Storage overage info. `null` when the sub doesn't track storage charges (legacy pro /
   *  business plans only) or the required fields aren't all populated. */
  storageOverage: StorageOverageInfo | null;
};

/**
 * Derives display + pricing info for a subscription against its plan. Returns `null` when the
 * inputs don't line up (no billing cycle, or the sub's billing cycle isn't on the plan), so
 * callers can no-render gracefully rather than crash on partial data.
 *
 * Only covers the static plan-shape join. Invoice/proration math lives elsewhere — those need
 * the upcoming-invoice query and should stay in their own hook.
 */
export const getSubscriptionInfo = ({
  subscription,
  plan,
  totalBillableSeats,
}: {
  subscription: Subscription | undefined;
  plan: PlanPricing | undefined;
  /** Number of billable seats on the workspace. Multiplied into `totalPriceCents` for
   *  perMember pricing models (e.g. Business). Ignored otherwise — pass `0` if irrelevant. */
  totalBillableSeats: number;
}): SubscriptionInfo | null => {
  if (!subscription || !plan) {
    return null;
  }

  const billingCyclePricing = subscription.billingCycle
    ? plan.billingCycles.find((cycle) => cycle.id === subscription.billingCycle)
    : null;

  if (subscription.billingCycle && !billingCyclePricing) {
    return null;
  }

  const planCredits = subscription.creditsPerCycle ?? 0;
  const creditPack = getSubscriptionCreditPackInfo({ subscription, plan });
  const storageOverage = getStorageOverageInfo({ subscription, plan, billingCyclePricing });
  const planPriceCents = billingCyclePricing?.planPrice ?? 0;
  const planTotalPriceCents =
    subscription.pricingModel === 'perMember' ? planPriceCents * totalBillableSeats : planPriceCents;

  return {
    billingCycle: billingCyclePricing?.id ?? null,
    planPriceCents,
    planCredits,
    creditPack,
    storageOverage,
    totalCredits: planCredits + (creditPack?.credits ?? 0),
    totalPriceCents: planTotalPriceCents + (creditPack?.totalPriceCents ?? 0),
  };
};

/**
 * Overlays the workspace-scoped current plan onto the public plans catalog so consumers see
 * contract-accurate pricing (custom Stripe prices, fixed-pro legacy plans, etc.) for the
 * subscriber's own tier without affecting the published prices shown for other tiers.
 *
 * Returns the input list unchanged if either input is missing — surfaces consuming this must
 * still gate on the catalog being loaded.
 */
export const overlayCurrentPlanPricing = ({
  plans,
  currentPlan,
  currentPlanId,
}: {
  plans: PlanPricing[] | undefined;
  currentPlan: PlanPricing | undefined;
  currentPlanId: SubscriptionPlan | undefined;
}): PlanPricing[] | undefined => {
  if (!plans || !currentPlan || !currentPlanId) {
    return plans;
  }

  return plans.map((p) => (p.id === currentPlanId ? currentPlan : p));
};

/**
 * Normalizes the plan-only price to a monthly rate (cents). Useful for surfaces that compare
 * spend across cycles (e.g. Canny's `monthlySpend`). Excludes credit packs and storage overage
 * by design. Returns `undefined` when the inputs aren't complete enough to compute (free sub,
 * cycle mismatch, etc.).
 */
export const getMonthlyPlanPriceCents = ({
  subscription,
  plan,
  totalBillableSeats,
}: {
  subscription: Subscription | undefined;
  plan: PlanPricing | undefined;
  totalBillableSeats: number;
}): number | undefined => {
  const info = getSubscriptionInfo({ subscription, plan, totalBillableSeats });

  if (!info?.billingCycle || !subscription) {
    return undefined;
  }

  const cycleTotal =
    subscription.pricingModel === 'perMember' ? info.planPriceCents * totalBillableSeats : info.planPriceCents;

  return Math.round(cycleTotal / PLAN_CYCLE_MONTHS[info.billingCycle]);
};
